/**
 * Finance: collecting money, the till, what families owe, and the
 * approvals that change it.
 *
 * Part of the route table (see ./index.ts). Split by domain so adding a
 * screen touches one small file instead of one long one.
 */
import { lazy } from "react";
import type { AppRoute } from "./types";

export const FINANCE_ROUTES: AppRoute[] = [
  {
    path: "/payments",
    title: "Saisir un paiement",
    component: lazy(() => import("@/pages/payments/PaymentsPage")),
    // ONLY the write code. `perms` is an OR, so listing the read code here let
    // anyone who may merely SEE payments open the till screen — which the
    // read-only Founder role made visible: an owner who cannot collect a franc
    // was being shown "Saisir un paiement". A screen whose entire purpose is a
    // write is gated on that write, never on a read that happens to be nearby.
    perms: ["finance.payment.collect.cash"],
    nav: { label: "Paiement", order: 4, mobile: 3, group: "Finance" },
  },
  {
    path: "/cashier",
    title: "Caisse",
    component: lazy(() => import("@/pages/cashier/CashierPage")),
    perms: ["finance.cash.session.open", "finance.cash.session.verify"],
    nav: { label: "Caisse", order: 5, mobile: 4, group: "Finance" },
  },
  {
    // /gestionnaire was the shipped cash-session screen. It IS /cashier —
    // opening, closing and verifying a till — so it points at the same
    // component rather than being ported a second time.
    path: "/gestionnaire",
    title: "Caisse",
    component: lazy(() => import("@/pages/cashier/CashierPage")),
    perms: [
      "finance.cash.session.open",
      "finance.cash.session.close",
      "finance.cash.session.verify",
    ],
  },
  {
    path: "/receipts",
    title: "Reçus",
    component: lazy(() => import("@/pages/receipts/ReceiptsPage")),
    perms: ["finance.payment.view.org"],
    nav: { label: "Reçus", order: 32, group: "Finance" },
  },
  {
    path: "/fees",
    title: "Types de frais",
    component: lazy(() => import("@/pages/fees/FeesPage")),
    perms: ["finance.fee_type.list", "finance.fee_type.view"],
    nav: { label: "Frais", order: 31, group: "Finance" },
  },
  {
    path: "/ledger",
    title: "Grand livre",
    component: lazy(() => import("@/pages/ledger/LedgerPage")),
    perms: ["finance.ledger.view"],
    nav: { label: "Grand livre", order: 30, group: "Finance" },
  },
  {
    path: "/finance-hub",
    title: "Finance",
    component: lazy(() => import("@/pages/finance-hub/FinanceHubPage")),
    perms: ["finance.report.view"],
    nav: { label: "Pôle finance", order: 33, group: "Finance" },
  },
  {
    path: "/approvals",
    title: "Approbations",
    component: lazy(() => import("@/pages/director/ApprovalsPage")),
    perms: ["finance.obligation.adjust.queue.view"],
    nav: { label: "Approbations", order: 4.9, group: "Finance" },
  },
  {
    // The shipped app called this /director. Kept as an alias so a bookmark
    // survives the cutover — same screen, one implementation.
    path: "/director",
    title: "Approbations",
    component: lazy(() => import("@/pages/director/ApprovalsPage")),
    perms: ["finance.obligation.adjust.queue.view"],
  },
  {
    path: "/proofs",
    title: "Preuves Mobile Money",
    component: lazy(() => import("@/pages/mobile-money-proofs/ProofsPage")),
    perms: ["finance.payment.view.org"],
    nav: { label: "Preuves Mobile Money", order: 34, group: "Finance" },
  },
  {
    path: "/automatic-payments",
    title: "Paiements automatiques",
    component: lazy(() => import("@/pages/automatic-payments/AutomaticPaymentsPage")),
    perms: ["finance.automatic_payments.view"],
    nav: { label: "Paiements automatiques", order: 35, group: "Finance" },
  },
];
